/**
 * «Где нужно разобраться» — главный блок Обзора района (план §1, блок 3).
 *
 * Три колонки внутри одной `Card`: список хозяйств 3/12 · разбор 5/12 ·
 * карта 4/12 (с 1280px; уже — список лентой сверху, разбор и карта 7/5).
 * Верхние категории (Все · Сроки · Вода · Расхождения · Карантин) — ТОЛЬКО
 * фильтр списка хозяйств: линза уходит серверу (`TAB_LENS`), счётчики —
 * хозяйства линзы. «Все» — объединение категорий (линза `any`,
 * agroai-6al.35), порядок — по критичности сервера.
 *
 * Разбор выбранного хозяйства (agroai-0e8): шапка (хозяйство, ссылка на
 * карточку) и ВКЛАДКИ НАПРАВЛЕНИЙ — только те, где у хозяйства есть
 * проблема (`farmDirections`, те же числа, что значки строки). По умолчанию —
 * выбранная категория, иначе главный сигнал строки. Вкладка направления
 * переключает и детализацию, и карту справа: другое направление того же
 * хозяйства смотрится без смены категории.
 *
 * Логика очереди та же, что у `SituationBoard` (v1 `/season` его и
 * продолжает рисовать): строки и счётчики считает сервер, диспозиция —
 * `SituationDisposition`, поручение — `assignFromSituationHref`.
 *
 * API:
 *   <ProblemsBoard scope={ProblemScope} season="2026" massifId={null}
 *                  tab={tab} onTab={setTab} farmId={"7"|null} onFarm={…}
 *                  waterGapByFarm={Map<actorId, м³>} districtFarms={by_farm} />
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, ChevronDown } from 'lucide-react';
import { api, type QueryParams, type Schemas } from '../../../api/client';
import { useRequest } from '../../../api/useRequest';
import { SituationDisposition } from '../../data-source/api/SituationDisposition';
import { toSituationsView } from '../../data-source/api/situations';
import { ChoiceSelect } from '../../ui/AnalysisControls';
import { Card } from '../../ui/Card';
import { EmptyState } from '../../ui/EmptyState';
import { LoadError, failed } from '../../ui/LoadError';
import { Icon } from '../../ui/Icon';
import { Skeleton, SkeletonList, SkeletonMap, SkeletonRegion, SkeletonText } from '../../ui/Skeleton';
import { countOf, dateDay, num, WORDS } from '../../lib/format';
import { tr } from '../../../i18n/runtime';
import {
  actionTitle,
  dispositionTally,
  defaultDirection,
  directionCount,
  explainFor,
  farmDirections,
  PROBLEM_LABEL,
  reviewKindFor,
  signalCounts,
  TABS,
  TAB_COUNT_NOTE,
  TAB_LENS,
  tabCount,
  visibleRows,
  visibleTabs,
  WHAT_TO_CHECK,
  type Direction,
  type ProblemTab,
} from './model';
import { AssignButton, KIND_ICON } from './ProblemCard';
import { ProblemMap } from './ProblemMap';
import { ReviewByKind } from './ReviewByKind';
import { SignalChips } from './SignalChips';
import { useFarmReview, workCycle, type ProblemScope } from './useFarmReview';
import './problems.css';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';

const m = defineMessages({
  queueLabel: { id: 'board.problemsBoard.queueLabel', defaultMessage: 'очередь «Где нужно разобраться»', description: 'Что грузится / не загрузилось (винительный падеж): подставляется в «Не удалось загрузить …» и в подпись скелетона для диктора' },
  title: { id: 'board.problemsBoard.title', defaultMessage: 'Где нужно разобраться', description: 'Заголовок главного блока Обзора района (менеджер): очередь хозяйств с проблемами' },
  tally: { id: 'board.problemsBoard.tally', defaultMessage: 'На {date}: новых {fresh} · в работе {inWork} · закрыто {closed}', description: 'Строка справа в шапке блока: сводка очереди по состоянию разбора; {date} — день и месяц, остальное — числа хозяйств' },
  showClosed: { id: 'board.problemsBoard.showClosed', defaultMessage: 'показать закрытые', description: 'Подпись флажка в шапке блока: показывать ли закрытые строки очереди; строчными' },
  tabsAria: { id: 'board.problemsBoard.tabsAria', defaultMessage: 'Тип проблемы', description: 'Подпись для экранного диктора: ряд вкладок типов проблем' },
  emptyTitle: { id: 'board.problemsBoard.emptyTitle', defaultMessage: 'Хозяйств для разбора нет', description: 'Заголовок пустого состояния очереди' },
  emptyReason: { id: 'board.problemsBoard.emptyReason', defaultMessage: 'По этой вкладке на дату состояния сигналов нет. Отсутствие сигнала не означает, что всё проверено.', description: 'Пояснение пустого состояния очереди, если сервер своего не прислал' },
  listAria: { id: 'board.problemsBoard.listAria', defaultMessage: 'Хозяйства', description: 'Подпись для экранного диктора: список хозяйств очереди' },
  due: { id: 'board.problemsBoard.due', defaultMessage: 'до {date}', description: 'Метка срока в строке хозяйства; {date} — день и месяц ближайшего срока; коротко' },
  closed: { id: 'board.problemsBoard.closed', defaultMessage: 'закрыто', description: 'Метка в строке хозяйства: строка закрыта; коротко, строчными' },
  inWork: { id: 'board.problemsBoard.inWork', defaultMessage: 'в работе', description: 'Метка в строке хозяйства: строкой уже занимаются; коротко, строчными' },
  more: { id: 'board.problemsBoard.more', defaultMessage: 'Ещё {count}', description: 'Кнопка под списком хозяйств: показать ещё; {count} — сколько осталось; рядом стрелка' },
  loadedOf: { id: 'board.problemsBoard.loadedOf', defaultMessage: 'Загружены {loaded} из {total} хозяйств вкладки.', description: 'Примечание под списком: загружена только часть очереди; {loaded}/{total} — числа' },
  context: { id: 'board.problemsBoard.context', defaultMessage: 'Разбор хозяйства', description: 'Подпись над именем хозяйства в центральной колонке разбора' },
  check: { id: 'board.problemsBoard.check', defaultMessage: 'Что проверить.', description: 'Жирное вводное слово перед описанием действия в разборе' },
  grounds: { id: 'board.problemsBoard.grounds', defaultMessage: 'Основания по работам:', description: 'Подпись перед ссылками на работы, из-за которых хозяйство попало в очередь' },
  groundItem: { id: 'board.problemsBoard.groundItem', defaultMessage: '{name} · {count}', description: 'Ссылка на работу-основание: название работы и число сигналов по ней' },
  farmLink: { id: 'board.problemsBoard.farmLink', defaultMessage: 'Карточка хозяйства →', description: 'Ссылка внизу разбора: открыть карточку хозяйства; стрелка в конце' },
  mapWork: { id: 'board.problemsBoard.mapWork', defaultMessage: 'Работа на карте', description: 'Подпись выпадающего списка над картой: какую работу показывать на карте' },
  mapWorkOverdue: { id: 'board.problemsBoard.mapWorkOverdue', defaultMessage: '{count} после срока', description: 'Описание пункта списка работ над картой: сколько работ на полях после срока; {count} — число' },
  dirsAria: { id: 'board.problemsBoard.dirsAria', defaultMessage: 'Направления хозяйства', description: 'Подпись для экранного диктора: ряд вкладок направлений (сроки, вода, расхождения, карантин) в разборе хозяйства' },
  dirsNone: { id: 'board.problemsBoard.dirsNone', defaultMessage: 'Сигналов по направлениям у хозяйства на эту дату нет.', description: 'Разбор хозяйства: у строки нет ни одного направления с проблемой' },
  farmFacts: { id: 'board.problemsBoard.farmFacts', defaultMessage: '{contours} · {directions, plural, one {# направление} few {# направления} many {# направлений} other {# направления}} с проблемой', description: 'Строка под именем хозяйства в разборе: сколько у него контуров и в скольких направлениях есть проблема; {contours} — «663 контура»' },
  farmFactsNoContours: { id: 'board.problemsBoard.farmFactsNoContours', defaultMessage: '{directions, plural, one {# направление} few {# направления} many {# направлений} other {# направления}} с проблемой', description: 'Строка под именем хозяйства в разборе, когда число контуров не названо' },
  mapWorkNoReport: { id: 'board.problemsBoard.mapWorkNoReport', defaultMessage: 'без отчёта', description: 'Описание пункта списка работ над картой: работа без отчёта' },
});

const PAGE = 60;
const ROWS = 6;

/**
 * Тело доски в загрузке: те же три колонки (список · разбор · карта) и та же
 * высота, что у готовой доски, — данные встают на место без сдвига (6al.49).
 */
export function ProblemsBoardBodySkeleton({ quiet }: { quiet?: boolean }) {
  return (
    <SkeletonRegion label={tr(m.queueLabel)} quiet={quiet} className="pboard">
      <SkeletonList quiet items={ROWS - 2} className="pboard__list" />
      <div className="pboard__review" aria-hidden="true">
        <Skeleton width="45%" className="skel--label" />
        <Skeleton variant="block" width="80%" height={28} />
        <SkeletonText lines={2} />
        <Skeleton variant="block" width="100%" height={72} />
        <Skeleton variant="block" width="100%" height={120} />
      </div>
      <div className="pboard__map"><SkeletonMap quiet height="100%" /></div>
    </SkeletonRegion>
  );
}

export interface ProblemsBoardProps {
  scope: ProblemScope;
  /** Ось сезона очереди («2026»): очередь читает сезон, а не цикл. */
  season: string | null;
  massifId: number | null;
  tab: ProblemTab;
  onTab: (tab: ProblemTab) => void;
  farmId: string | null;
  onFarm: (farmId: string) => void;
  waterGapByFarm?: ReadonlyMap<number, number>;
  districtFarms?: readonly Schemas['SeasonBreakdownRowOut'][] | null;
  id?: string;
}

export function ProblemsBoard({ scope, season, massifId, tab, onTab, farmId, onFarm, waterGapByFarm, districtFarms, id }: ProblemsBoardProps) {
  const t = useT();
  const [showClosed, setShowClosed] = useState(false);
  const [limit, setLimit] = useState(ROWS);
  const [quarantineKnown, setQuarantineKnown] = useState(false);
  // Линзу карантина спрашиваем, только когда сервер её назвал (6al.2):
  // старый сервер на незнакомую линзу ответил бы ошибкой.
  const lens = tab === 'quarantine' && !quarantineKnown ? TAB_LENS.all : TAB_LENS[tab];
  const query: QueryParams = useMemo(
    () => ({
      ...scope.params,
      ...(season ? { season } : {}),
      ...(massifId !== null ? { massif_id: massifId } : {}),
    }),
    [scope.params, scope.cycle, season, massifId],
  );
  // «Куда ехать» — по гектарам работ с наступившим сроком без «выполнено»
  // (решение №663): крупная несделанная площадь выше мелкой просрочки.
  const list = useRequest(() => api.situations({ ...query, lens, limit: PAGE, order: 'area' }), [JSON.stringify(query), lens]);
  const view = useMemo(() => (list.data ? toSituationsView(list.data) : null), [list.data]);
  useEffect(() => { if (typeof view?.byLens.quarantine === 'number') setQuarantineKnown(true); }, [view]);
  useEffect(() => { setLimit(ROWS); }, [tab, showClosed]);

  // Пока очередь не пришла, вкладки стоят полным рядом (со «Карантином»):
  // иначе ряд перестраивался, когда сервер называл линзу карантина.
  const tabs = view ? visibleTabs(view.byLens) : TABS.map(t => t.id);
  const effectiveTab: ProblemTab = tabs.includes(tab) ? tab : 'all';
  const all = view?.items ?? [];
  const rows = visibleRows(all, showClosed);
  // Итоги отметок — сервера, по всей очереди вкладки; счёт по загруженной
  // странице (60 из 168) остаётся только для ответа без итогов (agroai-b48.3).
  const tally = view?.queueTally ?? dispositionTally(all);
  const current = rows.find(r => r.farmId === farmId) ?? rows[0] ?? null;
  // Направления хозяйства — вкладки центра; выбор держится, пока то же
  // хозяйство и та же категория (новая категория — снова её направление).
  const dirs: Direction[] = current ? farmDirections(current, effectiveTab) : [];
  const [dirPick, setDirPick] = useState<{ farm: string; tab: ProblemTab; dir: Direction } | null>(null);
  const kind: Direction = current && dirPick && dirPick.farm === current.farmId && dirPick.tab === effectiveTab && dirs.includes(dirPick.dir)
    ? dirPick.dir
    : current ? defaultDirection(current, effectiveTab, dirs) : 'deadlines';
  const farmNum = current ? Number(current.farmId) : null;
  const review = useFarmReview(kind, Number.isInteger(farmNum) ? farmNum : null, scope);
  const campaignOf = new Map(review.works.map(w => [w.workDefId, { id: w.campaignId, cycle: workCycle(scope, w.cycle) }]));

  const aside = (
    <span className="pboard__aside">
      {view ? <span>{t(m.tally, { date: dateDay(scope.asOf), fresh: num(tally.fresh), inWork: num(tally.inWork), closed: num(tally.closed) })}</span> : <Skeleton width={260} className="skel--caption" />}
      <label className="pboard__toggle"><input type="checkbox" checked={showClosed} onChange={e => setShowClosed(e.target.checked)} /> {t(m.showClosed)}</label>
    </span>
  );

  return (
    <Card size="lg" id={id} className="pboard-card" title={t(m.title)} aside={aside} info={t(TAB_COUNT_NOTE)}>
      <div className="pboard__tabs" role="tablist" aria-label={t(m.tabsAria)} data-guide="lenses">
        {TABS.filter(x => tabs.includes(x.id)).map(x => {
          const count = tabCount(x.id, view?.byLens);
          return (
            <button key={x.id} type="button" role="tab" aria-selected={effectiveTab === x.id} className="pboard__tab" onClick={() => onTab(x.id)}>
              {x.id !== 'all' ? <Icon icon={KIND_ICON[x.id]} size={16} /> : null}
              {t(x.name)}
              <span className="pboard__count">{!view && !failed(list) ? <Skeleton width={24} className="skel--caption" /> : count === null ? '—' : num(count)}</span>
            </button>
          );
        })}
      </div>

      {failed(list) ? (
        <LoadError what={t(m.queueLabel)} error={list.error} onRetry={list.reload} />
      ) : !view ? (
        <ProblemsBoardBodySkeleton />
      ) : !current ? (
        <EmptyState title={t(m.emptyTitle)} reason={view.emptyNote || t(m.emptyReason)} />
      ) : (
        <div className="pboard" data-guide="situations">
          <aside className="pboard__list" aria-label={t(m.listAria)}>
            {rows.slice(0, limit).map(r => {
              const rowKind = reviewKindFor(effectiveTab, r);
              return (
                <button
                  key={r.key ?? r.farmId}
                  type="button"
                  className="prow"
                  aria-pressed={r.farmId === current.farmId}
                  onClick={() => onFarm(r.farmId)}
                >
                  <span className="prow__name">{r.name}<ArrowRight size={15} aria-hidden /></span>
                  <span className="prow__title">{actionTitle(rowKind, r)}</span>
                  <span className="prow__foot">
                    <SignalChips counts={signalCounts(r, waterGapByFarm?.get(Number(r.farmId)) ?? null)} />
                    {r.nextDeadline && r.nextDeadline >= scope.asOf ? <span className="prow__due">{t(m.due, { date: dateDay(r.nextDeadline) })}</span> : null}
                    {r.disposition ? <span className="prow__due">{r.disposition.status === 'closed' ? t(m.closed) : t(m.inWork)}</span> : null}
                  </span>
                </button>
              );
            })}
            {rows.length > limit ? (
              <button type="button" className="pboard__more" onClick={() => setLimit(n => n + ROWS)}>{t(m.more, { count: num(rows.length - limit) })} <ChevronDown size={15} /></button>
            ) : null}
            {view.total > all.length ? <p className="pboard__note">{t(m.loadedOf, { loaded: num(all.length), total: num(view.total) })}</p> : null}
          </aside>

          <article className="pboard__review" data-guide="evidence">
            <header className="pfarm">
              <span className="pboard__context">{t(m.context)}</span>
              <h3 className="pfarm__name">
                <Link to={`/farms/${current.farmId}${scope.linkSearch()}`}>{current.name} <ArrowUpRight size={18} aria-hidden /></Link>
              </h3>
              <p className="pfarm__facts">
                {typeof current.contoursTotal === 'number'
                  ? t(m.farmFacts, { contours: countOf(current.contoursTotal, WORDS.contour), directions: dirs.length })
                  : t(m.farmFactsNoContours, { directions: dirs.length })}
                {current.disposition ? <span className="prow__due"> · {current.disposition.status === 'closed' ? t(m.closed) : t(m.inWork)}</span> : null}
              </p>
            </header>

            {dirs.length ? (
              <div className="pdir" role="tablist" aria-label={t(m.dirsAria)} data-guide="directions">
                {dirs.map(d => {
                  const count = directionCount(d, current);
                  return (
                    <button
                      key={d}
                      id={`pdir-${d}`}
                      type="button"
                      role="tab"
                      aria-selected={kind === d}
                      aria-controls="pdir-panel"
                      className={`pdir__tab pdir__tab--${d}`}
                      onClick={() => setDirPick({ farm: current.farmId, tab: effectiveTab, dir: d })}
                    >
                      <span className="pdir__label">{t(PROBLEM_LABEL[d])}</span>
                      {count ? <span className="pdir__count">{count}</span> : null}
                    </button>
                  );
                })}
              </div>
            ) : <p className="review__empty">{t(m.dirsNone)}</p>}

            <section className="pdir__panel" id="pdir-panel" role="tabpanel" aria-labelledby={`pdir-${kind}`}>
              <h4 className="pboard__h"><Icon icon={KIND_ICON[kind]} size={20} />{actionTitle(kind, current)}</h4>
              <p className="pboard__explain">{explainFor(kind, current)}</p>
              <p className="pcard__check"><strong>{t(m.check)}</strong> {t(WHAT_TO_CHECK[kind])}</p>
              <ReviewByKind kind={kind} review={review} scope={scope} farmId={Number(current.farmId)} situation={current} districtFarms={districtFarms} />
            </section>

            <footer className="pfarm__foot">
              <SituationDisposition situation={current} params={query} onDone={list.reload} />
              <div className="pboard__actions" data-guide="action"><AssignButton situation={current} /></div>
              {current.relatedWorks.length ? (
                <div className="pboard__grounds">
                  <span>{t(m.grounds)}</span>
                  {current.relatedWorks.map(w => {
                    const campaign = campaignOf.get(Number(w.id));
                    return campaign?.id && campaign.cycle
                      ? <Link key={w.id} to={`/cycles/${campaign.cycle}/works/${campaign.id}${scope.linkSearch()}`}>{t(m.groundItem, { name: w.name, count: num(w.signals) })} <ArrowUpRight size={13} /></Link>
                      : <span key={w.id}>{t(m.groundItem, { name: w.name, count: num(w.signals) })}</span>;
                  })}
                </div>
              ) : null}
              <Link className="pboard__farmlink" to={`/farms/${current.farmId}${scope.linkSearch()}`}>{t(m.farmLink)}</Link>
            </footer>
          </article>

          <div className="pboard__map">
            {kind === 'deadlines' && review.works.some(w => w.overdue > 0 || w.noReport > 0) ? (
              <ChoiceSelect
                label={t(m.mapWork)}
                value={String(review.mapWorkId ?? '')}
                options={review.works.filter(w => w.overdue > 0 || w.silentContours.length > 0).map(w => ({ id: String(w.workDefId), name: w.name, description: w.overdue ? t(m.mapWorkOverdue, { count: num(w.overdue) }) : t(m.mapWorkNoReport) }))}
                onChange={v => review.setMapWorkId(Number(v))}
              />
            ) : null}
            <ProblemMap {...review.map} height="fill" farmName={current.name} linkSearch={scope.linkSearch()} />
          </div>
        </div>
      )}
    </Card>
  );
}
